import React, { Fragment, useState, useEffect, useRef } from "react";
import { useFetchVessels } from "../Utils/useFetchVessels";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { HiX } from "react-icons/hi";
import { FaCircle } from "react-icons/fa";
import { FiFilter } from "react-icons/fi";
import { RiNotification3Line } from "react-icons/ri";
import Skeleton from "react-loading-skeleton";
import { CiWarning } from "react-icons/ci";
import { FiAlertCircle } from "react-icons/fi";
import { useDeleteVessel } from "../Utils/useDeleteVessel";
import UpdatePrimaryVesselForm from "../updatevessels/updateprimaryinfo";
import UpdateVesselInformationForm from "../updatevessels/UpdateVesselInformationForm";
import UpdateVesselImagesForm from "../updatevessels/UpdateVesselImagesForm";
import UpdateVesselDocumentsForm from "../updatevessels/UpdateVesselDocumentsForm";
import UpdateVesselOtherDocumentsForm from "../updatevessels/UpdateVesselOtherDocumentsForm";
import VesselPrimaryInformation from "../mydetails/VesselPrimaryInformation";
import VesselInformation from "../mydetails/VesselInformation";
import VesselImages from "../mydetails/VesselImages";
import VesselsDocs from "../mydetails/myvesseldocs";
import OtherDocuments from "../mydetails/OtherDocuments";
// import PendingUpdates from "../pendingupdate/pendingupdate";
import { toast, ToastContainer } from "react-toastify";
import { RiMore2Fill } from "react-icons/ri";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import { ReactComponent as Logo } from "../../Asset/black.svg";
import "./myvessel.css";

const MyVessels = () => {
  const navigate = useNavigate();
  const { data: vessels, isLoading, error } = useFetchVessels();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVesselId, setSelectedVesselId] = useState(null);
  const [editOverlay, setEditOverlay] = useState(false);
  const [deleteOverlay, setDeleteOverlay] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [vesselDetailsOverlay, setVesselDetailsOverlay] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDropdown, setShowDropdown] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  const formatPrice = (price) => {
    return price.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  // Filter vessels based on search query and status filter
  const filteredVessels = vessels?.data.filter((vessel) => {
    // First apply the search filter
    const searchMatches = 
      (vessel.primaryInformation?.vesselType?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (vessel.vesselInformation?.mmsiNumber?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (vessel.vesselInformation?.imoNumber?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (vessel.primaryInformation?.details?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (vessel.stage?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    
    // Then apply the status filter using the active field from API
    if (statusFilter === "all") {
      return searchMatches;
    } else if (statusFilter === "currently-in-use") {
      return searchMatches && vessel.active === "busy";
    } else if (statusFilter === "idle") {
      return searchMatches && vessel.active === "idle";
    } else if (statusFilter === "available") {
      return searchMatches && vessel.active === "available";
    }
    
    return searchMatches;
  });

  // Sort function for the vessel data
  const sortedVessels = React.useMemo(() => {
    let sortableVessels = [...(filteredVessels || [])];
    if (sortConfig.key !== null) {
      sortableVessels.sort((a, b) => {
        let aValue, bValue;
        
        // Get the correct values based on the sort key
        switch(sortConfig.key) {
          case 'name':
            aValue = a.primaryInformation?.vesselName || '';
            bValue = b.primaryInformation?.vesselName || '';
            break;
          case 'mmsi':
            aValue = a.vesselInformation?.mmsiNumber || '';
            bValue = b.vesselInformation?.mmsiNumber || '';
            break;
          case 'imo':
            aValue = a.vesselInformation?.imoNumber || '';
            bValue = b.vesselInformation?.imoNumber || '';
            break;
          case 'type':
            aValue = a.primaryInformation?.vesselType || '';
            bValue = b.primaryInformation?.vesselType || '';
            break;
          case 'status':
            aValue = a.active || 'available';
            bValue = b.active || 'available';
            break;
          case 'pricing':
            aValue = a.primaryInformation?.pricing || 0;
            bValue = b.primaryInformation?.pricing || 0;
            break;
          case 'details':
            aValue = a.primaryInformation?.details || '';
            bValue = b.primaryInformation?.details || '';
            break;
          case 'stage':
            aValue = a.stage || '';
            bValue = b.stage || '';
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }
        
        // Compare the values
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableVessels;
  }, [filteredVessels, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const { mutate: deleteVessel } = useDeleteVessel();

  const handleDeleteVessel = (vesselId) => {
    setIsDeleting(true);
    deleteVessel(vesselId, {
      onSuccess: () => {
        setIsDeleting(false);
        setDeleteOverlay(false);
        toast.success("Delete Process Started, Awaiting Admin Approval!");
        setSelectedVesselId(null);
        localStorage.removeItem("selectedVesselType");
      },
      onError: (error) => {
        setIsDeleting(false);
        toast.error(`Error deleting vessel: ${error.message}`);
      },
    });
  };

  const handleActionClick = (vesselId, index, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right - 150 // 150 is dropdown width
    });
    setShowDropdown(showDropdown === index ? null : index);
    setSelectedVesselId(vesselId);
  };

  const handleTabClick = (filter) => {
    setStatusFilter(filter);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper to format the sort key for display
  const formatSortKey = (key) => {
    if (!key) return '';
    
    // Map keys to display names
    const displayNames = {
      'name': 'Vessel Name',
      'mmsi': 'MMSI Number',
      'imo': 'IMO Number',
      'type': 'Vessel Type',
      'status': 'Status',
      'pricing': 'Pricing',
      'details': 'Details',
      'stage': 'Stage'
    };
    
    return displayNames[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <Fragment>
      <ToastContainer />
      {vesselDetailsOverlay && (
        <div className="vessel-details-overlay">
          <div className="header">
           <Logo />
            <h2>Vessel Details</h2>
           <HiX
              className="icon"
              onClick={() => {
                setSelectedVesselId(null);
                setVesselDetailsOverlay(false);
              }}
            />
          </div>
          <div className="card">
            <h2>Choose the vessel details you would like to view</h2>
            <div className="card">
              <VesselPrimaryInformation vesselId={selectedVesselId} />
              <VesselInformation vesselId={selectedVesselId} />
              <VesselImages vesselId={selectedVesselId} />
              <VesselsDocs vesselId={selectedVesselId} />
              <OtherDocuments vesselId={selectedVesselId} />
              {/* <PendingUpdates  vesselId={selectedVesselId}/> */}
            </div>
          </div>
        </div>
      )}
      {deleteOverlay && (
        <div className="delete-vessel">
          <div className="card">
            <CiWarning className="warning" />
            <h2>Delete</h2>
            <p>Are you sure you want to delete the selected vessel?</p>
            <div className="btn">
              <button
                onClick={() => {
                  setDeleteOverlay(false);
                  setSelectedVesselId(null);
                  localStorage.removeItem("selectedVesselType");
                }}
              >
                Cancel
              </button>
              <button onClick={() => handleDeleteVessel(selectedVesselId)}>
                {isDeleting ? "Deleting..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}
      {editOverlay && selectedVesselId && (
        <div className="edit-vessel-container">
          <div className="header">
            <Logo />
            <h2>Quick edit</h2>
            <HiX
              className="icon"
              onClick={() => {
                setSelectedVesselId(null);
                setEditOverlay(false);
                localStorage.removeItem("selectedVesselType");
              }}
            />
            
          </div>
          <div className="card">
            <h2>
              Choose the type of vessel information you would like to update.
            </h2>
            <UpdatePrimaryVesselForm vesselId={selectedVesselId} />
            <UpdateVesselInformationForm vesselId={selectedVesselId} />
            <UpdateVesselImagesForm vesselId={selectedVesselId} />
            <UpdateVesselDocumentsForm vesselId={selectedVesselId} />
            <UpdateVesselOtherDocumentsForm vesselId={selectedVesselId} />
          </div>
        </div>
      )}
      <div className="vessel-list-container">
        <div className="vessel-list-header">
          <h2>Vessel list</h2>
          <div className="header-buttons">
            <button 
              className="pending-updates-button" 
              onClick={() => navigate("/dashboard/allpendingupdate")}
            >
              <RiNotification3Line /> Pending Updates
            </button>
            <button 
              className="new-vessel-button" 
              onClick={() => navigate("/create_vessel")}
            >
              <span>+</span> New Vessel
            </button>
          </div>
        </div>
        
        <div className="vessel-list-tabs">
          <button 
            className={`tab-button ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleTabClick('all')}
          >
            All
          </button>
          <button 
            className={`tab-button ${statusFilter === 'available' ? 'active' : ''}`}
            onClick={() => handleTabClick('available')}
          >
            Available
          </button>
          <button 
            className={`tab-button ${statusFilter === 'currently-in-use' ? 'active' : ''}`}
            onClick={() => handleTabClick('currently-in-use')}
          >
            Currently in use
          </button>
          <button 
            className={`tab-button ${statusFilter === 'idle' ? 'active' : ''}`}
            onClick={() => handleTabClick('idle')}
          >
            Idle
          </button>
        </div>
        
        <div className="vessel-list-actions">
          <div className="search-sort-container">
            <div className="sort-container" ref={sortRef}>
              <button 
                className="sort-button" 
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                <FiFilter /> Sort by
                {sortConfig.key && (
                  <span className="current-sort">
                    {formatSortKey(sortConfig.key)}
                    <span className="sort-direction-indicator">
                      {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                    </span>
                  </span>
                )}
              </button>
              {showSortDropdown && (
                <div className="sort-dropdown">
                  <button 
                    className={`sort-option ${sortConfig.key === 'name' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('name');
                      setShowSortDropdown(false);
                    }}
                  >
                    Vessel Name
                    {sortConfig.key === 'name' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'mmsi' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('mmsi');
                      setShowSortDropdown(false);
                    }}
                  >
                    MMSI Number
                    {sortConfig.key === 'mmsi' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'imo' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('imo');
                      setShowSortDropdown(false);
                    }}
                  >
                    IMO Number
                    {sortConfig.key === 'imo' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'type' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('type');
                      setShowSortDropdown(false);
                    }}
                  >
                    Vessel Type
                    {sortConfig.key === 'type' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'status' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('status');
                      setShowSortDropdown(false);
                    }}
                  >
                    Status
                    {sortConfig.key === 'status' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'pricing' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('pricing');
                      setShowSortDropdown(false);
                    }}
                  >
                    Pricing
                    {sortConfig.key === 'pricing' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'details' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('details');
                      setShowSortDropdown(false);
                    }}
                  >
                    Details
                    {sortConfig.key === 'details' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                  <button 
                    className={`sort-option ${sortConfig.key === 'stage' ? 'active' : ''}`} 
                    onClick={() => {
                      requestSort('stage');
                      setShowSortDropdown(false);
                    }}
                  >
                    Stage
                    {sortConfig.key === 'stage' && (
                      <span className="sort-direction-indicator">
                        {sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="search-container">
              <input
                type="text" 
                placeholder="Search vessels"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="search-button">
                <CiSearch />
              </button>
            </div>
          </div>
        </div>
        
        <div className="vessel-table">
          <div className="vessel-table-header">
            <div className="vessel-header-cell">IMAGE</div>
            <div className="vessel-header-cell">MMSI NUMBER</div>
            <div className="vessel-header-cell">IMO NUMBER</div>
            <div className="vessel-header-cell">TYPE</div>
            <div className="vessel-header-cell">STATUS</div>
            <div className="vessel-header-cell">PRICING</div>
            <div className="vessel-header-cell">STAGE</div>
            <div className="vessel-header-cell actions">ACTION</div>
          </div>
          
              {isLoading ? (
            <div className="loading-skeleton">
              {[1, 2, 3].map((item) => (
                <div className="vessel-row skeleton" key={item}>
                  <div className="vessel-cell vessel-info">
                    <Skeleton width={48} height={48} borderRadius={4} />
                    <Skeleton width={120} height={20} />
                  </div>
                  <div className="vessel-cell">
                    <Skeleton width={100} height={20} />
                  </div>
                  <div className="vessel-cell">
                    <Skeleton width={100} height={20} />
                  </div>
                  <div className="vessel-cell vessel-type">
                    <Skeleton width={100} height={20} />
                  </div>
                  <div className="vessel-cell vessel-status">
                    <Skeleton width={140} height={20} />
                  </div>
                  <div className="vessel-cell vessel-pricing">
                    <Skeleton width={80} height={20} />
                  </div>
                  <div className="vessel-cell">
                    <Skeleton width={80} height={20} />
                  </div>
                  <div className="vessel-cell actions">
                    <Skeleton width={24} height={24} circle={true} />
                  </div>
                </div>
              ))}
            </div>
              ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FiAlertCircle size={48} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 18, fontWeight: 500 }}>Pending Approval</div>
              <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>Your vessels are being reviewed.</div>
            </div>
          ) : (
            sortedVessels.map((vessel, index) => (
              <div className="vessel-row" key={vessel._id}>
                <div className="vessel-cell vessel-info">
                  <img 
                    src={vessel.images[0] || "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAclBMVEX///8AAADCwsLt7e1zc3NVVVVDQ0OEhIR5eXlZWVnLy8vo6Oipqan39/ff39+7u7sUFBSSkpJsbGwsLCzU1NSjo6M6OjqwsLDh4eH09PRLS0s/Pz+Li4ubm5tmZma9vb0PDw8iIiI2NjZ/f38pKSlfX1/NzXY4AAADyElEQVR4nO3c6XaqMBiFYZwqaj1qxQGtVmu9/1s8JthWZUhiAySs9/nVhQGzZQh8QIMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABUadi2alh3nkfjZcuyeVx3pnsftgO2Wq91Z7o3tJ9wUXemB93GJwzijj2jLxcTWjUnofecTrg69f9u6nDCXXOPpVebxifsW0vo3Jnpt/6LIFflfPNiSp7ebsUCVnUHURE9faKTazHfyH53SiB6+sS58z8x3z/73SkBCXOsfU24PnX17D1NODYbJzxMGDU+Ydz4hEG0H+jZ+JpQm7fHUm3NHw9J6BASXowzRA1KWHAW0JCEBbdyGpJw2viEiwoTjs5b43pKtp1BwiCapH0sSkm4zf81TU0MEmaSo8XaYjhhfLCXcPazVPOEndFo1JH3IYfiT20aCQv2+QoTvj/7nUtlRplwcAofnI5i7tTkMJT3Qs+pyafpnxKun/9ZQ62EH+np4lLtlNFeXs5mFKTn2Qmj7tuN/Wyc3QvDMsCtN62Ek/T0vIQdsdS2bsLRY3/21hO+15tw9difQ0HCydBQ+/trakyY2r+mBQmNy/lycFHewik3YdA+/1ZhlrYTTrTmKjnhrVfbCcW9vLmyVYUJF7YTij4OlK08Thgf7r4zj8cJh1oHmuRKzdKI/3txIRql7pAW7IfyqNhRd/bOTMyUcwpxsepdiWbLQe/BQAT/Sk2+tn9JTR2ILebzu/0ga6HyWNrKWOKFLHZvbiZkbFUpYnm93E9LeJrQrkgZUJ4v7XI/ntWdQEX9tInY6Asul+09FlIS9ZX+y6XVMf9jcRF0DDVvzVYvVG+k8oSwYKx4u3x8Vi7FZX3FmvY+oRzuNwUNvE8ojzNFY0pX8Qs4TzwpPc0f7oMgvDTYVtYd++QqzB8Mg6S29VlVd+xLKoSFb52IR12PRSvZbe+KoSK4vuViu7pcmaTAVdxGr8ThKnmirihBxco91WHyBo7yOLn1d0CU25/64iNUjSfOGouhUF0ITipxXu6IZ61t9LojKu9qOCiU26hOTUeWI/zbTJN3VjIKS2krjSOue5L7/+qdUBJNc0pfzkrWYFez9czg13BE8qqq9iAX6w0r7ohlkVKjjv9DbtNT0zpsbaKj2RoU5Ondp2Nvvue51j/NBrjkFP3gwyXG8CsJqL4R8zBfMtvO4ovYpYh61yqq+UnYJK8g66TNM8eMdt29NvDk6cna+j/tKEn3+YO+vbeTS7T/0xOLsd3/LVMGP94rBQAAAAAAAAAAAAAAAAAAAAAAAAAAAADASf8B6TJF0n5mnu0AAAAASUVORK5CYII="} 
                    alt={vessel.primaryInformation?.vesselType || "Vessel"} 
                    className="vessel-thumbnail"
                  />
                  {/* <div>{vessel.primaryInformation?.vesselName || "N/A"}</div> */}
                </div>
                <div className="vessel-cell">
                  {vessel.vesselInformation?.mmsiNumber || "N/A"}
                </div>
                <div className="vessel-cell">
                  {vessel.vesselInformation?.imoNumber || "N/A"}
                </div>
                <div className="vessel-cell vessel-type">
                  {vessel.primaryInformation?.vesselType || "AHTS 001"}
                </div>
                <div className="vessel-cell vessel-status">
                  <span className={`status-indicator ${vessel.active === "busy" ? "active" : vessel.active === "available" ? "available" : "idle"}`}>
                    <FaCircle className="status-dot" />
                    {vessel.active === "busy" ? "Currently in use" : vessel.active === "available" ? "Available" : "Idle"}
                  </span>
                </div>
                <div className="vessel-cell vessel-pricing">
                  <span className="pricing-text">{formatPrice(vessel.primaryInformation?.pricing || 10000)}</span>
                </div>
                <div className="vessel-cell">
                  {vessel.stage || "Active"}
                </div>
                <div className="vessel-cell actions">
                  <button 
                    className="action-button" 
                    onClick={(e) => handleActionClick(vessel._id, index, e)}
                  >
                    <RiMore2Fill />
                  </button>
                  
                  {showDropdown === index && (
                    <div 
                      ref={dropdownRef}
                      className="action-dropdown"
                      style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`
                      }}
                    >
                      {!vessel.createdVesselStepStatus?.complete && (
                        <button
                          className="dropdown-item continue-creation"
                          onClick={() => {
                            localStorage.setItem("vesselId", vessel._id);
                            localStorage.setItem(
                              "selectedVesselType",
                              vessel.primaryInformation?.vesselType
                            );
                            const stepPages = [
                              "vessel_general_information",
                              "vesselimages",
                              "veseelprimarydoc",
                              "vesselotherdocs",
                            ];

                            // Get step status safely
                            let stepStatus = vessel.createdVesselStepStatus?.status || "1";

                            // Convert status to index (ensure it's within bounds)
                            let stepIndex = parseInt(stepStatus, 10) - 1;
                            stepIndex = stepIndex >= 0 && stepIndex < stepPages.length ? stepIndex : 0;

                            // Get next step page
                            const nextStepPage = stepPages[stepIndex];
                            
                            setShowDropdown(null);
                            navigate(`/${nextStepPage}`);
                          }}
                        >
                          Continue Creation
                        </button>
                      )}
                      <button
                        className="dropdown-item" 
                        onClick={() => {
                          setEditOverlay(true);
                          setShowDropdown(null);
                          localStorage.setItem(
                            "selectedVesselType",
                            vessel.primaryInformation?.vesselType || "Not Provided"
                          );
                        }}
                      >
                        Quick Edit
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setVesselDetailsOverlay(true);
                          setShowDropdown(null);
                        }}
                      >
                        View Details
                      </button>
                      <button
                        className="dropdown-item danger"
                        onClick={() => {
                          setDeleteOverlay(true);
                          setShowDropdown(null);
                        }}
                      >
                        Unlist Vessel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
              )}
        </div>
      </div>
    </Fragment>
  );
};

export default MyVessels;
